// src/pages/CalculationHistory.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';

const CalculationHistory = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    fetchHistory();
  }, [page, rowsPerPage]);

  const fetchHistory = async () => {
  try {
    setLoading(true);
    
    // Get the auth token from localStorage
    const token = localStorage.getItem('token');
    
    if (!token) {
      setError('You must be logged in to view calculation history');
      setLoading(false);
      return;
    }
    
    const response = await axios.get('/api/calculations/history', {
      params: {
        offset: page * rowsPerPage,
        limit: rowsPerPage
      },
      headers: {
        'Authorization': `Bearer ${token}` // Add this header
      }
    });
    
    setHistory(response.data.history || []);
    setTotal(response.data.total || 0);
  } catch (err) {
    console.error('Error fetching calculation history:', err);
    setError('Failed to load calculation history: ' + (err.response?.data?.error || err.message));
  } finally {
    setLoading(false);
  }
};

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    const value = parseFloat(amount || 0);
    return `£${value.toFixed(2)}`;
  };

  const handleViewService = (serviceId) => {
    if (serviceId) {
      navigate(`/calculator/services/${serviceId}`);
    }
  };

  return (
    <div className="page-container">
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 4, borderRadius: 2 }}>
          <Typography variant="h5" component="h1" gutterBottom>
            Calculation History
          </Typography>
          
          <Divider sx={{ mb: 3 }} />
          
          {loading && history.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          ) : history.length === 0 ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              No calculation history found. Start calculating service costs to build your history.
            </Alert>
          ) : (
            <>
              <TableContainer>
                <Table aria-label="calculation history table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Service</TableCell>
                      <TableCell align="right">Product Cost</TableCell>
                      <TableCell align="right">Staff Cost</TableCell>
                      <TableCell align="right">Overhead</TableCell>
                      <TableCell align="right">Total Cost</TableCell>
                      <TableCell align="right">Recommended Price</TableCell>
                      <TableCell align="right">Price with VAT</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {history.map((item) => (
                      <TableRow key={item.id} hover>
                        <TableCell>{formatDate(item.calculation_date)}</TableCell>
                        <TableCell>{item.service_name || 'N/A'}</TableCell>
                        <TableCell align="right">{formatCurrency(item.product_cost)}</TableCell>
                        <TableCell align="right">{formatCurrency(item.time_cost)}</TableCell>
                        <TableCell align="right">{formatCurrency(item.overhead_cost)}</TableCell>
                        <TableCell align="right">{formatCurrency(item.total_cost)}</TableCell>
                        <TableCell align="right">{formatCurrency(item.recommended_price)}</TableCell>
                        <TableCell align="right">{formatCurrency(item.price_with_vat)}</TableCell>
                        <TableCell align="right">
                          {item.service_id && (
                            <Button 
                              size="small" 
                              variant="outlined"
                              color="primary"
                              onClick={() => handleViewService(item.service_id)}
                            >
                              View Service
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={total}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          )}
          
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
            <Button 
              variant="contained" 
              color="primary"
              onClick={() => navigate('/calculator')}
            >
              Return to Calculator
            </Button>
            
            {!loading && history.length > 0 && (
              <Button 
                variant="outlined"
                onClick={fetchHistory}
              >
                Refresh History
              </Button>
            )}
          </Box>
        </Paper>
        
        {/* Additional Information Card */}
        <Paper sx={{ p: 3, mt: 3, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom>
            About Calculation History
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            This page shows a complete history of all price calculations performed in your SalonLogic calculator.
            Each calculation record includes product costs, staff costs, overhead, and the recommended price.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You can click on "View Service" to navigate to the original service that was calculated.
            Use this history to track how your pricing strategies and costs have evolved over time.
          </Typography>
        </Paper>
      </Container>
    </div>
  );
};

export default CalculationHistory;